<template>
  <div class="py-4 container-fluid">

    <!-- ── 网站列表 ── -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <div>
              <h5 class="mb-0">网站管理</h5>
              <p class="mb-0 text-sm text-muted">管理投放网站、API配置及账号分配</p>
            </div>
            <div class="d-flex gap-2 flex-wrap">
              <button class="btn btn-success btn-sm mb-0" :disabled="syncing" @click="triggerSync('hourly')">
                {{ syncing ? '同步中...' : '同步今天' }}
              </button>
              <button class="btn btn-info btn-sm mb-0" :disabled="syncing" @click="triggerSync('weekly')">同步近7天</button>
              <button class="btn btn-outline-secondary btn-sm mb-0" @click="downloadTemplate">下载CSV模板</button>
              <input ref="csvInput" type="file" accept=".csv" style="display:none" @change="onCSVImport" />
              <button class="btn btn-warning btn-sm mb-0" :disabled="importing" @click="$refs.csvInput.click()">导入CSV</button>
              <button class="btn btn-danger btn-sm mb-0" @click="confirmClear">清空数据</button>
              <button class="btn btn-primary btn-sm mb-0" @click="openAdd">+ 添加网站</button>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">网站名称</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">上游</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">API 配置</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">同步</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">分配账号</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">备注</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="siteLoading">
                  <td colspan="7" class="text-center py-4 text-sm text-muted">加载中...</td>
                </tr>
                <tr v-else-if="websites.length === 0">
                  <td colspan="7" class="text-center py-4 text-sm text-muted">暂无数据</td>
                </tr>
                <tr v-for="site in websites" :key="site.id">
                  <td><p class="text-sm font-weight-bold mb-0">{{ site.name }}</p></td>
                  <td><p class="text-sm mb-0">{{ site.upstream_name }}</p></td>
                  <td>
                    <template v-if="site.api_type === 'tripit' || site.api_type === 'ys998802'">
                      <span class="badge bg-gradient-info me-1">ys998802</span>
                      <code v-if="site.api_username" class="text-xs">{{ site.api_username }}</code>
                      <span v-else class="text-xs text-danger">未配置账号</span>
                    </template>
                    <template v-else>
                      <code v-if="site.api_key" class="text-xs">{{ maskKey(site.api_key) }}</code>
                      <span v-else class="text-xs text-danger">未配置</span>
                    </template>
                  </td>
                  <td>
                    <span class="badge" :class="site.enabled ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                      {{ site.enabled ? '启用' : '停用' }}
                    </span>
                  </td>
                  <td>
                    <span v-for="n in site.user_names" :key="n" class="badge bg-gradient-secondary me-1">{{ n }}</span>
                    <span v-if="!site.user_names || site.user_names.length === 0" class="text-xs text-muted">未分配</span>
                  </td>
                  <td><p class="text-sm mb-0 text-muted">{{ site.remarks || '-' }}</p></td>
                  <td>
                    <button class="btn btn-link btn-sm mb-0 p-0 me-2 text-info" @click="openEdit(site)">编辑</button>
                    <button class="btn btn-link btn-sm mb-0 p-0 me-2 text-warning" @click="confirmClear(site)">清空数据</button>
                    <button class="btn btn-link btn-sm mb-0 p-0 text-danger" @click="confirmDelete(site)">删除</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ── 同步日志 ── -->
    <div class="row mt-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <h5 class="mb-0">数据同步日志</h5>
            <button class="btn btn-outline-secondary btn-sm mb-0" @click="loadLogs">刷新</button>
          </div>
          <div class="table-responsive">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">时间</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">网站</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">类型</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">日期范围</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">状态</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-end">写入条数</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">错误信息</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="logs.length === 0">
                  <td colspan="7" class="text-center py-4 text-sm text-muted">暂无记录</td>
                </tr>
                <tr v-for="l in logs" :key="l.id">
                  <td><p class="text-xs mb-0 text-muted">{{ l.created_at }}</p></td>
                  <td><p class="text-sm mb-0">{{ l.upstream_name }}</p></td>
                  <td>
                    <span class="badge" :class="logTypeBadge(l.fetch_type)">{{ logTypeLabel(l.fetch_type) }}</span>
                  </td>
                  <td><p class="text-xs mb-0">{{ l.date_range }}</p></td>
                  <td>
                    <span class="badge" :class="l.status === 'success' ? 'bg-gradient-success' : 'bg-gradient-danger'">
                      {{ l.status === 'success' ? '成功' : '失败' }}
                    </span>
                  </td>
                  <td class="text-end"><p class="text-sm font-weight-bold mb-0">{{ l.rows_upserted }}</p></td>
                  <td><p class="text-xs mb-0 text-danger">{{ l.message }}</p></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ── 网站 新增/编辑 弹窗 ── -->
    <div v-if="showSiteModal" class="modal-overlay" @click.self="showSiteModal = false">
      <div class="modal-box card p-4" style="width:520px;max-height:90vh;overflow-y:auto">
        <h6 class="mb-3">{{ isEdit ? '编辑网站' : '添加网站' }}</h6>
        <div v-if="siteFormError" class="alert alert-danger text-white text-sm py-2 mb-3">{{ siteFormError }}</div>

        <div class="mb-3">
          <label class="form-label text-sm">网站名称 <span class="text-danger">*</span></label>
          <input v-model="siteForm.name" type="text" class="form-control" placeholder="如 example.com" />
        </div>

        <div class="mb-3">
          <label class="form-label text-sm">上游 <span class="text-danger">*</span></label>
          <select v-model="siteForm.upstream_id" class="form-control">
            <option v-for="u in upstreams" :key="u.id" :value="u.id">{{ u.name }}</option>
          </select>
        </div>

        <hr class="horizontal dark my-3" />
        <p class="text-xs text-uppercase font-weight-bold text-secondary mb-3">API 配置</p>

        <div class="mb-3">
          <label class="form-label text-sm">API 类型</label>
          <select v-model="siteForm.api_type" class="form-control">
            <option value="ys998801">ys998801</option>
            <option value="tripit">ys998802</option>
          </select>
        </div>

        <div v-if="siteForm.api_type === 'tripit'" class="row mb-3">
          <div class="col-6">
            <label class="form-label text-sm">Tripit 用户名</label>
            <input v-model="siteForm.api_username" type="text" class="form-control" autocomplete="username" placeholder="请输入上游用户名" />
          </div>
          <div class="col-6">
            <label class="form-label text-sm">Tripit 密码</label>
            <input v-model="siteForm.api_password" type="password" class="form-control" autocomplete="current-password" placeholder="请输入上游密码" />
          </div>
          <div class="col-12 mt-2 d-flex align-items-center gap-2">
            <button class="btn btn-outline-info btn-sm mb-0" :disabled="tripitTesting" @click="testTripitConnection">
              {{ tripitTesting ? '认证中...' : '测试认证' }}
            </button>
            <span class="text-xs text-muted">先获取 Token，验证账号可用后再保存。</span>
          </div>
        </div>

        <div v-if="siteForm.api_type !== 'tripit'" class="mb-3">
          <label class="form-label text-sm">API Key</label>
          <div v-click-outside="() => showKeyDropdown = false" class="position-relative">
            <input
              v-model="siteForm.api_key"
              type="text"
              class="form-control"
              placeholder="请填写 API Key，或从历史记录中选择"
              @focus="showKeyDropdown = true"
              @input="showKeyDropdown = true"
            />
            <ul v-if="showKeyDropdown && filteredUsedKeys.length" class="key-dropdown list-unstyled mb-0">
              <li
                v-for="k in filteredUsedKeys"
                :key="k"
                class="key-dropdown-item"
                @mousedown.prevent="siteForm.api_key = k; showKeyDropdown = false"
              >
                <span class="text-xs text-muted me-2">历史</span>{{ k }}
              </li>
            </ul>
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label text-sm">自定义 API 地址（留空使用默认）</label>
          <input
v-model="siteForm.api_url" type="text" class="form-control"
            :placeholder="siteForm.api_type === 'tripit' ? 'https://admin.tripit.cc/api/thirdservice' : 'https://h5manager.top/prod-api/...'" />
        </div>

        <div class="mb-3">
          <label class="form-label text-sm">自动同步</label>
          <select v-model="siteForm.enabled" class="form-control">
            <option :value="1">启用</option>
            <option :value="0">停用</option>
          </select>
        </div>

        <hr class="horizontal dark my-3" />
        <p class="text-xs text-uppercase font-weight-bold text-secondary mb-3">收益分成设置</p>

        <div class="row mb-3">
          <div class="col-6">
            <label class="form-label text-sm">扣除eCPM比例（0~100%）</label>
            <div class="input-group">
              <input
v-model.number="siteForm.ecpm_deduction_pct" type="number" min="0" max="100" step="0.01"
                class="form-control" placeholder="0" />
              <span class="input-group-text">%</span>
            </div>
            <small class="text-muted text-xs">分成前收益 = 曝光数 × (1 - 此比例) × eCPM / 1000</small>
          </div>
          <div class="col-6">
            <label class="form-label text-sm">收益分成比例（0~100%）</label>
            <div class="input-group">
              <input
v-model.number="siteForm.revenue_share_pct" type="number" min="0" max="100" step="0.01"
                class="form-control" placeholder="0" />
              <span class="input-group-text">%</span>
            </div>
            <small class="text-muted text-xs">分成后收益 = (1 - 此比例) × 分成前收益</small>
          </div>
        </div>

        <hr class="horizontal dark my-3" />
        <p class="text-xs text-uppercase font-weight-bold text-secondary mb-3">账号分配</p>

        <div class="mb-3">
          <div class="border rounded p-3" style="max-height:140px;overflow-y:auto">
            <div v-if="normalUsers.length === 0" class="text-sm text-muted">暂无普通账号</div>
            <div v-for="u in normalUsers" :key="u.id" class="form-check mb-1">
              <input :id="'u-'+u.id" v-model="siteForm.user_ids" class="form-check-input" type="checkbox" :value="u.id" />
              <label :for="'u-'+u.id" class="form-check-label text-sm">{{ u.username }}</label>
            </div>
          </div>
        </div>

        <div class="mb-4">
          <label class="form-label text-sm">备注</label>
          <textarea v-model="siteForm.remarks" class="form-control" rows="2" placeholder="选填"></textarea>
        </div>

        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-sm btn-secondary mb-0" @click="showSiteModal = false">取消</button>
          <button class="btn btn-sm btn-primary mb-0" :disabled="saving" @click="saveSite">
            {{ saving ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── 删除确认 ── -->
    <div v-if="deleteTarget" class="modal-overlay" @click.self="deleteTarget = null">
      <div class="modal-box card p-4" style="width:360px">
        <h6 class="mb-2">确认删除</h6>
        <p class="text-sm mb-4">确定要删除网站 <strong>{{ deleteTarget.name }}</strong> 吗？</p>
        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-sm btn-secondary mb-0" @click="deleteTarget = null">取消</button>
          <button class="btn btn-sm btn-danger mb-0" :disabled="saving" @click="doDelete">确认删除</button>
        </div>
      </div>
    </div>

    <!-- 导入进度弹窗 -->
    <div v-if="importing" class="modal-overlay">
      <div class="modal-box card p-4" style="width:420px">
        <h6 class="mb-3">正在导入数据</h6>
        <p class="text-sm text-muted mb-2">共 {{ importProgress.total }} 行，正在写入数据库...</p>
        <div class="progress" style="height:10px">
          <div class="progress-bar progress-bar-striped progress-bar-animated bg-warning w-100"></div>
        </div>
        <p class="text-xs text-muted mt-2 mb-0">请勿关闭页面...</p>
      </div>
    </div>

    <!-- 清空数据确认弹窗 -->
    <div v-if="showClearConfirm" class="modal-overlay" @click.self="showClearConfirm = false">
      <div class="modal-box card p-4" style="width:380px">
        <h6 class="mb-2 text-danger">
          {{ clearTarget ? `确认清空「${clearTarget.name}」的收益数据？` : '确认清空所有收益数据？' }}
        </h6>
        <p class="text-sm mb-4">
          <template v-if="clearTarget">
            此操作将删除网站 <strong>{{ clearTarget.name }}</strong> 在 revenue_daily 表中的全部数据，不可恢复。
          </template>
          <template v-else>
            此操作将删除 revenue_daily 表中的<strong>全部数据</strong>，不可恢复。
          </template>
        </p>
        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-sm btn-secondary mb-0" @click="showClearConfirm = false">取消</button>
          <button class="btn btn-sm btn-danger mb-0" :disabled="clearing" @click="doClear">
            {{ clearing ? '清空中...' : '确认清空' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 导入结果弹窗 -->
    <div v-if="importResult" class="modal-overlay" @click.self="importResult = null">
      <div class="modal-box card p-4" style="width:480px;max-height:80vh;overflow-y:auto">
        <h6 class="mb-3">CSV 导入结果</h6>
        <div class="d-flex gap-3 mb-3">
          <span class="badge bg-gradient-success fs-6 px-3 py-2">成功 {{ importResult.success }} 行</span>
          <span v-if="importResult.failed > 0" class="badge bg-gradient-danger fs-6 px-3 py-2">失败 {{ importResult.failed }} 行</span>
        </div>
        <div v-if="importResult.errors && importResult.errors.length > 0">
          <p class="text-sm text-danger mb-2">错误详情：</p>
          <ul class="text-xs text-danger ps-3" style="max-height:200px;overflow-y:auto">
            <li v-for="(e, i) in importResult.errors" :key="i">{{ e }}</li>
          </ul>
        </div>
        <div class="text-end mt-3">
          <button class="btn btn-sm btn-primary mb-0" @click="importResult = null">确定</button>
        </div>
      </div>
    </div>

    <!-- 同步提示 -->
    <div v-if="syncMsg" class="position-fixed bottom-0 end-0 p-3" style="z-index:9999">
      <div class="alert alert-success text-white py-2 px-3 text-sm shadow">{{ syncMsg }}</div>
    </div>
  </div>
</template>

<script>
import { request } from "@/api/index.js";

export default {
  name: "WebsiteManage",
  directives: {
    clickOutside: {
      mounted(el, binding) {
        el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener("mousedown", el._clickOutside);
      },
      unmounted(el) { document.removeEventListener("mousedown", el._clickOutside); },
    },
  },
  data() {
    return {
      siteLoading: true,
      saving: false,
      tripitTesting: false,
      syncing: false,
      syncMsg: "",
      importing: false,
      importProgress: { total: 0, processed: 0, success: 0, failed: 0 },
      importResult: null,
      showClearConfirm: false,
      clearing: false,
      clearTarget: null,
      websites: [],
      upstreams: [],
      allUsers: [],
      logs: [],
      showSiteModal: false,
      isEdit: false,
      siteForm: {
        id: null, name: "", upstream_id: null,
        api_key: "", api_type: "ys998801", api_url: "", api_username: "", api_password: "", enabled: 1,
        ecpm_deduction_pct: 0, revenue_share_pct: 0,
        user_ids: [], remarks: "",
      },
      siteFormError: "",
      deleteTarget: null,
      showKeyDropdown: false,
    };
  },
  computed: {
    normalUsers() {
      return this.allUsers.filter(u => u.role_id !== 1);
    },
    filteredUsedKeys() {
      const all = [...new Set(this.websites.map(s => s.api_key).filter(Boolean))];
      const q = (this.siteForm.api_key || "").trim().toLowerCase();
      if (!q) return all;
      return all.filter(k => k.toLowerCase().includes(q));
    },
  },
  async created() {
    await Promise.all([this.loadWebsites(), this.loadUpstreams(), this.loadUsers(), this.loadLogs()]);
  },
  methods: {
    async testTripitConnection() {
      if (!this.siteForm.api_username || !this.siteForm.api_password) {
        this.siteFormError = "请填写 Tripit 用户名和密码后再测试";
        return;
      }
      this.tripitTesting = true;
      this.siteFormError = "";
      try {
        const { createTripitClient } = await import("@/api/tripit.js");
        await createTripitClient({
          baseUrl: this.siteForm.api_url,
          username: this.siteForm.api_username,
          password: this.siteForm.api_password,
        }).authenticate();
        this.siteFormError = "Tripit 认证成功，可以保存配置";
      } catch (e) {
        this.siteFormError = "Tripit 认证失败：" + e.message;
      } finally {
        this.tripitTesting = false;
      }
    },
    async loadWebsites() {
      try { this.websites = await request("/api/websites") || []; }
      catch (e) { console.error(e); } finally { this.siteLoading = false; }
    },
    async loadUpstreams() {
      try { this.upstreams = await request("/api/upstreams") || []; } catch (e) { console.error(e); }
    },
    async loadUsers() {
      try { this.allUsers = await request("/api/users") || []; } catch (e) { console.error(e); }
    },
    async loadLogs() {
      try { this.logs = await request("/api/fetch/logs") || []; } catch (e) { console.error(e); }
    },
    openAdd() {
      this.isEdit = false;
      this.siteForm = {
        id: null, name: "", upstream_id: this.upstreams[0]?.id || null,
        api_key: "", api_type: "ys998801", api_url: "", api_username: "", api_password: "", enabled: 1,
        ecpm_deduction_pct: 0, revenue_share_pct: 0,
        user_ids: [], remarks: "",
      };
      this.siteFormError = "";
      this.showSiteModal = true;
    },
    openEdit(site) {
      this.isEdit = true;
      this.siteForm = {
        id: site.id, name: site.name, upstream_id: site.upstream_id,
        api_key: site.api_key || "", api_type: site.api_type === "ys998802" ? "tripit" : (site.api_type || "ys998801"),
        api_url: site.api_url || "", api_username: site.api_username || "", api_password: "", enabled: site.enabled ?? 1,
        ecpm_deduction_pct: +(site.ecpm_deduction_ratio * 100).toFixed(4),
        revenue_share_pct: +(site.revenue_share_ratio * 100).toFixed(4),
        user_ids: [...(site.user_ids || [])], remarks: site.remarks || "",
      };
      this.siteFormError = "";
      this.showSiteModal = true;
    },
    async saveSite() {
      this.siteFormError = "";
      if (!this.siteForm.name) { this.siteFormError = "请填写网站名称"; return; }
      if (!this.siteForm.upstream_id) { this.siteFormError = "请选择上游"; return; }
      if (this.siteForm.api_type === "tripit" && !this.siteForm.api_username) {
        this.siteFormError = "请填写 ys998802 用户名";
        return;
      }
      if (this.siteForm.api_type === "tripit" && !this.isEdit && !this.siteForm.api_password) {
        this.siteFormError = "首次配置 ys998802 时必须填写密码";
        return;
      }
      this.saving = true;
      try {
        const body = {
          name: this.siteForm.name, upstream_id: this.siteForm.upstream_id,
          api_key: this.siteForm.api_key, api_type: this.siteForm.api_type,
          api_url: this.siteForm.api_url, enabled: this.siteForm.enabled,
          ecpm_deduction_ratio: (this.siteForm.ecpm_deduction_pct || 0) / 100,
          revenue_share_ratio: (this.siteForm.revenue_share_pct || 0) / 100,
          user_ids: this.siteForm.user_ids, remarks: this.siteForm.remarks,
        };
        if (this.siteForm.api_type === "tripit") {
          body.api_username = this.siteForm.api_username;
          if (this.siteForm.api_password) body.api_password = this.siteForm.api_password;
        }
        if (this.isEdit) {
          await request(`/api/websites/${this.siteForm.id}`, { method: "PUT", body: JSON.stringify(body) });
        } else {
          await request("/api/websites", { method: "POST", body: JSON.stringify(body) });
        }
        this.showSiteModal = false;
        await this.loadWebsites();
      } catch (e) { this.siteFormError = e.message; } finally { this.saving = false; }
    },
    confirmDelete(site) { this.deleteTarget = site; },
    async doDelete() {
      this.saving = true;
      try {
        await request(`/api/websites/${this.deleteTarget.id}`, { method: "DELETE" });
        this.deleteTarget = null;
        await this.loadWebsites();
      } catch (e) { alert(e.message); } finally { this.saving = false; }
    },
    async triggerSync(type) {
      this.syncing = true;
      try {
        const data = await request("/api/fetch/trigger", { method: "POST", body: JSON.stringify({ type }) });
        this.syncMsg = data?.msg || "同步任务已触发";
        setTimeout(() => { this.syncMsg = ""; }, 4000);
        setTimeout(() => this.loadLogs(), 3000);
      } catch (e) { alert(e.message); } finally { this.syncing = false; }
    },
    maskKey(key) {
      if (!key || key.length < 8) return key ? "****" : "";
      return key.slice(0, 4) + "****" + key.slice(-4);
    },
    logTypeBadge(t) {
      if (t === "hourly" || t === "manual-hourly") return "bg-gradient-primary";
      if (t === "weekly" || t === "manual-weekly") return "bg-gradient-info";
      return "bg-gradient-secondary";
    },
    logTypeLabel(t) {
      return { hourly: "每小时", weekly: "每8小时", "manual-hourly": "手动-今天", "manual-weekly": "手动-7天" }[t] || t;
    },
    downloadTemplate() {
      const headers = ["网站", "日期", "请求", "匹配请求", "曝光数", "点击数", "分成前收益", "广告位", "国家"];
      const sample  = ["example.com", "2026-05-17", "10000", "8500", "7200", "360", "90.00", "banner_top", "US"];
      const csv = [headers.join(","), sample.join(",")].join("\r\n");
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "revenue_import_template.csv"; a.click();
      URL.revokeObjectURL(url);
    },
    async onCSVImport(e) {
      const file = e.target.files[0];
      if (!file) return;

      // 本地计行数（快速，无需上传）
      const text = await file.text();
      const rowCount = Math.max(0, text.split("\n").filter((l) => l.trim()).length - 1);
      this.importProgress = { total: rowCount, processed: 0, success: 0, failed: 0 };
      this.importResult = null;
      this.importing = true;

      try {
        const formData = new FormData();
        formData.append("file", file);
        const token = localStorage.getItem("token");
        const res = await fetch("/api/revenue/import", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const raw = await res.text();
        let json;
        try { json = JSON.parse(raw); } catch {
          throw new Error(raw.includes("Proxy error") ? "无法连接后端，请先启动后端服务" : raw.slice(0, 150));
        }
        if (json.code !== 0) throw new Error(json.msg || "导入失败");
        const d = json.data;
        this.importProgress = { total: d.total, processed: d.total, success: d.success, failed: d.failed };
        this.importResult = { success: d.success, failed: d.failed, errors: d.errors || [] };
      } catch (err) {
        alert("导入失败: " + err.message);
      } finally {
        this.importing = false;
        this.$refs.csvInput.value = "";
      }
    },
    confirmClear(site = null) {
      this.clearTarget = site;
      this.showClearConfirm = true;
    },
    async doClear() {
      this.clearing = true;
      try {
        const body = this.clearTarget ? { website: this.clearTarget.name } : {};
        const data = await request("/api/revenue/clear", { method: "POST", body: JSON.stringify(body) });
        this.showClearConfirm = false;
        this.syncMsg = data?.msg || "清空完成";
        setTimeout(() => { this.syncMsg = ""; }, 4000);
      } catch (e) { alert("清空失败: " + e.message); } finally { this.clearing = false; }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center; z-index: 9999;
}
.modal-box { border-radius: 12px; }
.key-dropdown {
  position: absolute; top: 100%; left: 0; right: 0;
  background: #fff; border: 1px solid #dee2e6; border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,.12); z-index: 100;
  max-height: 180px; overflow-y: auto;
}
.key-dropdown-item {
  padding: 7px 12px; font-size: 12px; cursor: pointer;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  border-bottom: 1px solid #f0f0f0;
}
.key-dropdown-item:last-child { border-bottom: none; }
.key-dropdown-item:hover { background: #f5f7fa; }
</style>
